/*
 * laser-settings.js
 * Wires up the laser-settings page:
 *  1. Filter chips + search box filter the card grid in-place
 *  2. The "Other / not in this list" material option reveals a free-text field
 *  3. Submission form POSTs JSON to the configured Cloudflare Worker,
 *     surfaces inline success/error messages
 */
(function () {
  if (window.__laserSettingsWired) return;
  window.__laserSettingsWired = true;

  function init() {
    initFilters();
    initForm();
  }

  /* ---- 1. Filters + URL state + share ---- */
  // Soft palette for colour swatches keyed by canonical colour names.
  // Falls back to a neutral grey for unknown colours.
  const COLOUR_HEX = {
    black: '#1a1a1a',
    white: '#f7f3ec', off_white: '#f3eee5', ivory: '#f3eee5', cream: '#f3eee5',
    red: '#c0392b', scarlet: '#b13a2c', crimson: '#a82a3a',
    maroon: '#7b1e2e', burgundy: '#7b1e2e', wine: '#7b1e2e',
    pink: '#e91e63', rose: '#e91e63',
    orange: '#e67e22',
    peach: '#ff8a73', salmon: '#ff8a73', coral: '#ff8a73',
    yellow: '#f1c40f', lemon: '#f1c40f',
    gold: '#d4a73a',
    bronze: '#a86b2b', copper: '#a86b2b',
    green: '#27ae60', lime: '#27ae60',
    forest: '#2c5e3f', olive: '#5a6b2c', dark_green: '#2c5e3f',
    mint: '#9fdcc2', teal: '#2dd4bf',
    cyan: '#00bcd4', aqua: '#00bcd4',
    blue: '#2980b9',
    navy: '#1a3360', dark_blue: '#1a3360',
    sky: '#7ec5e8', light_blue: '#7ec5e8', baby_blue: '#7ec5e8',
    purple: '#8e44ad', violet: '#8e44ad',
    lavender: '#b39ddb', lilac: '#b39ddb',
    beige: '#d4b896', tan: '#d4b896',
    brown: '#8b6f47',
    gray: '#9aa0a6', grey: '#9aa0a6',
    silver: '#c0c0c0',
    clear: '#f0f0f0', transparent: '#f0f0f0', frosted: '#f0f0f0',
  };
  const LIGHT_COLOURS = new Set(['white', 'off_white', 'ivory', 'cream', 'silver', 'beige', 'tan', 'clear', 'transparent', 'frosted', 'mint', 'sky', 'light_blue', 'baby_blue']);

  function initFilters() {
    const grid = document.querySelector('[data-laser-settings-grid]');
    if (!grid) return;

    // State seeded from URL. Operation + laser type filters don't carry
    // an "Any" chip — every visitor lands on Cut / CO2 by default
    // (overwhelmingly the most common combo for this catalog).
    const params = new URLSearchParams(window.location.search);
    const DEFAULTS = { operation: 'Cut', type: 'CO2' };
    const state = {
      material: params.get('material') || '',
      colour: params.get('color') || '',
      operation: params.get('op') || DEFAULTS.operation,
      type: params.get('type') || DEFAULTS.type,
      brand: params.get('brand') || '',
      wattage: params.get('wattage') || '',
      thickness: params.get('thickness') || '',
      q: (params.get('q') || '').trim().toLowerCase(),
      favoritesOnly: params.get('fav') === '1',
    };

    // ---- Build the colour swatch row from card data ----
    // Each card carries `data-colours="black|red|white|"` pipe-joined.
    // We aggregate per material category so the swatch row only shows
    // colours that actually have results for the active material.
    function colourSetForMaterial(mat) {
      const set = new Set();
      grid.querySelectorAll('.laser-settings-card').forEach(card => {
        if (mat && card.dataset.material !== mat) return;
        const raw = card.dataset.colours || '';
        raw.split('|').forEach(c => { if (c) set.add(c.trim()); });
      });
      return [...set].sort();
    }

    const colourRow = grid.querySelector('[data-colour-row]');
    const colourList = grid.querySelector('[data-colour-list]');

    function renderColourSwatches() {
      if (!colourList) return;
      const colours = colourSetForMaterial(state.material);
      colourList.innerHTML = '';
      if (colours.length === 0) {
        if (colourRow) colourRow.hidden = true;
        return;
      }
      if (colourRow) colourRow.hidden = false;
      colours.forEach(name => {
        const key = name.toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
        const hex = COLOUR_HEX[key] || '#bdbdbd';
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'laser-settings-colour';
        if (LIGHT_COLOURS.has(key)) btn.classList.add('laser-settings-colour--light');
        if (state.colour && state.colour.toLowerCase() === name.toLowerCase()) {
          btn.classList.add('is-active');
        }
        btn.style.setProperty('--swatch-color', hex);
        btn.dataset.filter = 'colour';
        btn.dataset.value = name;
        btn.setAttribute('aria-label', name);
        btn.innerHTML = `<span class="laser-settings-colour-name">${name}</span>`;
        btn.addEventListener('click', () => {
          // Toggle off if same colour clicked
          state.colour = state.colour.toLowerCase() === name.toLowerCase() ? '' : name;
          renderColourSwatches();
          syncUrl();
          applyFilters();
        });
        colourList.appendChild(btn);
      });
    }

    // ---- Link buttons (tabs + filter rows) ----
    function setActive(filter, value) {
      grid.querySelectorAll(`[data-filter="${filter}"]`).forEach(b => {
        const isMatch = (b.dataset.value || '') === (value || '');
        b.classList.toggle('is-active', isMatch);
      });
    }

    grid.querySelectorAll('[data-filter]').forEach(btn => {
      const filter = btn.dataset.filter;
      if (filter === 'colour') return; // colour buttons are dynamic
      btn.addEventListener('click', () => {
        const value = btn.dataset.value || '';
        state[filter] = value;
        // Cascade-reset filters that are narrower than the one just
        // changed. The order goes material → type → brand → model;
        // picking something higher up resets everything below so the
        // user always lands on a sensible set of results instead of an
        // accidentally-empty grid.
        if (filter === 'material') {
          // Top-level tab change = clean slate within that material.
          // Everything in the secondary rows resets so the user sees
          // every setting that exists for the chosen material.
          state.colour = '';
          state.operation = '';
          state.type = '';
          state.brand = '';
          state.wattage = '';
          state.thickness = '';
          renderColourSwatches();
          setActive('operation', '');
          setActive('type', '');
          setActive('brand', '');
          setActive('wattage', '');
          setActive('thickness', '');
        }
        if (filter === 'brand') {
          state.wattage = '';
          setActive('wattage', '');
        }
        setActive(filter, value);
        syncDynamicChipVisibility();
        syncUrl();
        applyFilters();
      });
    });

    // ---- Dynamic chip visibility ----
    // Fixed rows (operation, laser type, wattage) are always shown so
    // the user can change those without losing their bearings. Dynamic
    // rows (material tabs, brand chips, thickness chips) hide chips
    // that would yield zero results given the current selections —
    // and the whole row collapses when nothing's left.
    function syncDynamicChipVisibility() {
      const allCards = [...grid.querySelectorAll('.laser-settings-card')];

      function matchesExcept(excludeRow) {
        return allCards.filter(c => {
          if (excludeRow !== 'material' && state.material && c.dataset.material !== state.material) return false;
          if (state.operation && c.dataset.operation !== state.operation.toLowerCase()) return false;
          if (state.type && c.dataset.type !== state.type.toLowerCase()) return false;
          if (excludeRow !== 'brand' && state.brand && c.dataset.brand !== state.brand.toLowerCase()) return false;
          if (state.wattage && !(c.dataset.wattage || '').split('|').includes(state.wattage)) return false;
          // Empty data-thickness is a wildcard (matches any thickness chip).
          if (excludeRow !== 'thickness' && state.thickness && c.dataset.thickness && c.dataset.thickness !== state.thickness) return false;
          return true;
        });
      }

      function syncRow(rowSelector, dataKey, getCardValue) {
        const chips = grid.querySelectorAll(rowSelector);
        if (!chips.length) return;
        const matches = matchesExcept(dataKey);
        let visibleNonAny = 0;
        chips.forEach(chip => {
          const val = chip.dataset.value || '';
          // "Any" / "All materials" always visible.
          if (val === '') { chip.hidden = false; return; }
          // The currently active chip stays visible so the user can
          // see what they picked and deselect from it.
          if (chip.classList.contains('is-active')) {
            chip.hidden = false;
            visibleNonAny++;
            return;
          }
          const hit = matches.some(c => getCardValue(c, val));
          chip.hidden = !hit;
          if (hit) visibleNonAny++;
        });
        // Hide the surrounding row if nothing but the "Any" chip would
        // show. For material tabs the parent is the <nav>; for chip
        // rows it's the .laser-settings-filters__row wrapper.
        const wrap = chips[0].closest('[data-filter-row], .laser-settings-filters__row, .laser-settings-tabs');
        if (wrap) wrap.hidden = visibleNonAny === 0;
      }

      // Material tabs are top-level navigation and stay visible at all
      // times even if a particular category has no matches in the
      // current filter state — they should always be reachable so the
      // user can switch between materials.
      syncRow('[data-filter="brand"]', 'brand',
        (c, val) => c.dataset.brand === val.toLowerCase());
      syncRow('[data-filter="thickness"]', 'thickness',
        (c, val) => c.dataset.thickness === val);
    }

    // ---- Search input ----
    const search = grid.querySelector('[data-laser-settings-search]');
    if (search) {
      search.value = state.q;
      let t;
      search.addEventListener('input', e => {
        clearTimeout(t);
        t = setTimeout(() => {
          state.q = (e.target.value || '').trim().toLowerCase();
          syncUrl();
          applyFilters();
        }, 150);
      });
    }

    // ---- Share view button (page-level) ----
    // Copies the current URL — syncUrl() keeps it in lockstep with the
    // active filters, so whatever you see is what the recipient lands on.
    const shareBtn = grid.querySelector('[data-laser-settings-share]');
    if (shareBtn) {
      shareBtn.addEventListener('click', async () => {
        const url = window.location.href;
        try {
          await navigator.clipboard.writeText(url);
        } catch (_) {
          const ta = document.createElement('textarea');
          ta.value = url;
          ta.style.position = 'fixed';
          ta.style.opacity = '0';
          document.body.appendChild(ta);
          ta.select();
          try { document.execCommand('copy'); } catch (_) {}
          document.body.removeChild(ta);
        }
        shareBtn.classList.add('is-copied');
        setTimeout(() => shareBtn.classList.remove('is-copied'), 1600);
      });
    }

    // ---- Reset button ----
    const reset = grid.querySelector('[data-laser-settings-reset]');
    if (reset) {
      reset.addEventListener('click', () => {
        state.material = '';
        state.colour = '';
        // Operation + type return to their defaults rather than empty
        // because the chip rows don't have an "Any" option to land on.
        state.operation = DEFAULTS.operation;
        state.type = DEFAULTS.type;
        state.brand = '';
        state.wattage = '';
        state.thickness = '';
        state.q = '';
        state.favoritesOnly = false;
        if (search) search.value = '';
        setActive('material', '');
        setActive('operation', state.operation);
        setActive('type', state.type);
        setActive('brand', '');
        setActive('wattage', '');
        setActive('thickness', '');
        renderColourSwatches();
        syncDynamicChipVisibility();
        const favF = grid.querySelector('[data-laser-fav-filter]');
        if (favF) favF.classList.remove('is-active');
        syncUrl();
        applyFilters();
      });
    }

    // ---- Favourites filter chip ----
    const favFilter = grid.querySelector('[data-laser-fav-filter]');
    if (favFilter) {
      favFilter.classList.toggle('is-active', state.favoritesOnly);
      favFilter.addEventListener('click', () => {
        state.favoritesOnly = !state.favoritesOnly;
        favFilter.classList.toggle('is-active', state.favoritesOnly);
        // Turning favourites ON: clear the material tab so the user sees
        // ALL their saved settings, not just the ones in the currently
        // active category.
        if (state.favoritesOnly) {
          state.material = '';
          setActive('material', '');
          state.colour = '';
          renderColourSwatches();
        }
        syncUrl();
        applyFilters();
      });
    }
    // Re-apply filters whenever a heart is toggled (count changes, and
    // if favoritesOnly is on we need to re-render the visible set).
    document.addEventListener('laser-favorites:updated', () => {
      if (state.favoritesOnly) applyFilters();
    });

    function syncUrl() {
      const p = new URLSearchParams();
      if (state.material) p.set('material', state.material);
      if (state.colour) p.set('color', state.colour);
      // Only persist op/type when they deviate from the default so the
      // URL stays clean on first load.
      if (state.operation && state.operation !== DEFAULTS.operation) p.set('op', state.operation);
      if (state.type && state.type !== DEFAULTS.type) p.set('type', state.type);
      if (state.brand) p.set('brand', state.brand);
      if (state.wattage) p.set('wattage', state.wattage);
      if (state.thickness) p.set('thickness', state.thickness);
      if (state.q) p.set('q', state.q);
      if (state.favoritesOnly) p.set('fav', '1');
      const newSearch = p.toString();
      const next = window.location.pathname + (newSearch ? '?' + newSearch : '') + window.location.hash;
      history.replaceState(null, '', next);
    }

    function applyFilters() {
      const cards = grid.querySelectorAll('.laser-settings-card');
      let visible = 0;
      const colourLower = state.colour.toLowerCase();
      const favHas = window.LaserFavorites && window.LaserFavorites.has;
      cards.forEach(card => {
        const matchMat = !state.material || card.dataset.material === state.material;
        const matchOp = !state.operation || card.dataset.operation === state.operation.toLowerCase();
        const matchType = !state.type || card.dataset.type === state.type.toLowerCase();
        const matchBrand = !state.brand || card.dataset.brand === state.brand.toLowerCase();
        const matchWattage = !state.wattage || (card.dataset.wattage || '').split('|').includes(state.wattage);
        // A card with an empty data-thickness means the source entry
        // didn't specify a thickness (common for engrave-style settings
        // that work the same across stock). Treat empty as "applies to
        // any thickness" so those cards survive a thickness chip.
        const matchThickness = !state.thickness || !card.dataset.thickness || card.dataset.thickness === state.thickness;
        const matchQ = !state.q || (card.dataset.search || '').includes(state.q);
        const matchColour = !colourLower || (card.dataset.colours || '').toLowerCase().includes(colourLower);
        const settingId = card.querySelector('.js-laser-fav-btn')?.dataset?.settingId;
        const matchFav = !state.favoritesOnly || (favHas ? favHas(settingId) : false);
        const show = matchMat && matchOp && matchType && matchBrand && matchWattage && matchThickness && matchQ && matchColour && matchFav;
        card.hidden = !show;
        // Count underlying settings (variants), not cards — the denominator
        // "Y settings" in the header counts raw settings too, so both sides
        // need to be in the same units.
        if (show) visible += parseInt(card.dataset.variantCount, 10) || 1;
      });
      const counter = grid.querySelector('[data-laser-settings-visible]');
      if (counter) counter.textContent = visible;
      const empty = grid.querySelector('[data-laser-settings-empty-results]');
      if (empty) empty.hidden = visible !== 0;
    }

    // ---- Initial paint: hydrate UI from state then filter ----
    setActive('material', state.material);
    setActive('operation', state.operation);
    setActive('type', state.type);
    setActive('brand', state.brand);
    setActive('wattage', state.wattage);
    setActive('thickness', state.thickness);
    renderColourSwatches();
    syncDynamicChipVisibility();
    syncUrl();
    applyFilters();
  }

  /* ---- 2. Submission form ---- */
  function initForm() {
    const section = document.querySelector('[data-laser-settings-form-section]');
    if (!section) return;
    const form = section.querySelector('[data-laser-settings-form]');
    if (!form) return;

    const workerUrl = section.dataset.workerUrl;
    const turnstileKey = section.dataset.turnstileKey;
    const msg = form.querySelector('[data-laser-settings-message]');
    const submitBtn = form.querySelector('.laser-settings-form__submit');

    // Material typeahead — hits /search/suggest.json so the full
    // catalog is searchable without Liquid's per-collection size cap.
    // Selecting a suggestion writes the product id into the hidden
    // material_id input; the visible text input stays as the display
    // label so the user can re-edit.
    const materialSearch = form.querySelector('[data-laser-settings-material-search]');
    const materialSuggest = form.querySelector('[data-laser-settings-material-suggestions]');
    const materialInput = form.querySelector('[data-laser-settings-material-input]');
    const materialClear = form.querySelector('[data-laser-settings-material-clear]');
    const otherWrap = form.querySelector('[data-laser-settings-other]');
    const otherToggle = form.querySelector('[data-laser-settings-other-toggle]');
    const otherInput = otherWrap ? otherWrap.querySelector('input[name="material_name"]') : null;

    function setMaterialSelection(product) {
      if (!materialInput || !materialSearch) return;
      materialInput.value = product.id;
      materialSearch.value = product.title;
      materialSearch.dataset.selectedTitle = product.title;
      if (materialClear) materialClear.hidden = false;
      hideSuggestions();
    }

    function clearMaterialSelection() {
      if (materialInput) materialInput.value = '';
      if (materialSearch) {
        materialSearch.value = '';
        delete materialSearch.dataset.selectedTitle;
      }
      if (materialClear) materialClear.hidden = true;
    }

    function hideSuggestions() {
      if (!materialSuggest) return;
      materialSuggest.hidden = true;
      materialSuggest.innerHTML = '';
      if (materialSearch) materialSearch.setAttribute('aria-expanded', 'false');
    }

    function renderSuggestions(products) {
      if (!materialSuggest) return;
      materialSuggest.innerHTML = '';
      if (!products.length) {
        const li = document.createElement('li');
        li.className = 'laser-settings-material-search__empty';
        li.textContent = 'No matches. Try a different term or use "Enter it manually" below.';
        materialSuggest.appendChild(li);
        materialSuggest.hidden = false;
        if (materialSearch) materialSearch.setAttribute('aria-expanded', 'true');
        return;
      }
      products.forEach(p => {
        const li = document.createElement('li');
        li.className = 'laser-settings-material-search__option';
        li.setAttribute('role', 'option');
        li.dataset.productId = p.id;
        li.innerHTML = `
          <span class="laser-settings-material-search__thumb">${
            p.featured_image && p.featured_image.url
              ? `<img src="${p.featured_image.url}" alt="" loading="lazy">`
              : `<span aria-hidden="true">${(p.title || '?').slice(0, 1).toUpperCase()}</span>`
          }</span>
          <span class="laser-settings-material-search__title">${p.title || ''}</span>
        `;
        li.addEventListener('mousedown', (e) => {
          // mousedown fires before the input's blur, so the selection
          // sticks even though blur would otherwise hide suggestions.
          e.preventDefault();
          setMaterialSelection({ id: p.id, title: p.title });
        });
        materialSuggest.appendChild(li);
      });
      materialSuggest.hidden = false;
      if (materialSearch) materialSearch.setAttribute('aria-expanded', 'true');
    }

    let searchTimer = null;
    let lastQuery = '';
    async function runSearch(query) {
      const q = query.trim();
      if (q.length < 2) {
        hideSuggestions();
        return;
      }
      if (q === lastQuery) return;
      lastQuery = q;
      try {
        const url = `/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=10&resources[options][fields]=title,product_type,vendor,tag`;
        const res = await fetch(url, { headers: { 'Accept': 'application/json' } });
        const data = await res.json();
        const products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
        // Ignore stale responses if the user typed something newer.
        if (q !== lastQuery) return;
        renderSuggestions(products);
      } catch (_) {
        // Network hiccup — silent. User can retry.
      }
    }

    if (materialSearch) {
      materialSearch.addEventListener('input', (e) => {
        // Editing after a selection invalidates the picked product.
        if (materialSearch.dataset.selectedTitle &&
            materialSearch.value !== materialSearch.dataset.selectedTitle) {
          if (materialInput) materialInput.value = '';
          delete materialSearch.dataset.selectedTitle;
          if (materialClear) materialClear.hidden = true;
        }
        clearTimeout(searchTimer);
        searchTimer = setTimeout(() => runSearch(e.target.value), 180);
      });
      materialSearch.addEventListener('focus', () => {
        if (materialSearch.value.trim().length >= 2) runSearch(materialSearch.value);
      });
      materialSearch.addEventListener('blur', () => {
        // Delay so a click on a suggestion can still register.
        setTimeout(hideSuggestions, 150);
      });
    }

    if (materialClear) {
      materialClear.addEventListener('click', () => {
        clearMaterialSelection();
        if (materialSearch) materialSearch.focus();
      });
    }

    if (otherToggle) {
      otherToggle.addEventListener('click', () => {
        if (!otherWrap) return;
        const willShow = otherWrap.hidden;
        otherWrap.hidden = !willShow;
        otherToggle.textContent = willShow
          ? 'Found it in the catalog? Search above ←'
          : "Can't find it? Enter it manually →";
        if (willShow && otherInput) otherInput.focus();
      });
    }

    // Lazy-load Turnstile only when a site key is configured
    if (turnstileKey && !window.__turnstileLoaded) {
      window.__turnstileLoaded = true;
      const s = document.createElement('script');
      s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      s.async = true;
      s.defer = true;
      document.head.appendChild(s);
    }

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (!workerUrl) {
        showMessage('error', 'Submissions endpoint not configured.');
        return;
      }

      // Honeypot — silently succeed without sending
      if (form.querySelector('input[name="website"]').value) {
        showMessage('success', 'Thanks!');
        return;
      }

      const payload = collectPayload(form);
      if (!payload.thickness || !payload.operation || !payload.power_percent || !payload.speed) {
        showMessage('error', 'Fill in the required fields.');
        return;
      }

      // Turnstile token, if Turnstile is loaded
      if (turnstileKey && window.turnstile) {
        try {
          payload.turnstile_token = await new Promise((resolve, reject) => {
            window.turnstile.render(document.createElement('div'), {
              sitekey: turnstileKey,
              callback: resolve,
              'error-callback': () => reject(new Error('turnstile')),
            });
            setTimeout(() => reject(new Error('turnstile timeout')), 10000);
          });
        } catch (_) {
          // soft-fail; Worker can reject if it requires the token
        }
      }

      submitBtn.disabled = true;
      submitBtn.classList.add('is-loading');
      showMessage('', '');

      try {
        const res = await fetch(workerUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const data = await res.json().catch(() => ({}));

        if (res.ok) {
          showMessage('success', data.message || 'Thanks! Your submission is in review.');
          form.reset();
          if (otherWrap) otherWrap.hidden = true;
        } else {
          showMessage('error', data.error || `Submission failed (HTTP ${res.status}).`);
        }
      } catch (err) {
        showMessage('error', 'Network error. Try again in a moment.');
      } finally {
        submitBtn.disabled = false;
        submitBtn.classList.remove('is-loading');
      }
    });

    function showMessage(type, text) {
      if (!msg) return;
      msg.textContent = text;
      msg.classList.remove('laser-settings-form__message--success', 'laser-settings-form__message--error');
      if (type === 'success') msg.classList.add('laser-settings-form__message--success');
      if (type === 'error') msg.classList.add('laser-settings-form__message--error');
    }
  }

  function collectPayload(form) {
    const fd = new FormData(form);
    const obj = {};
    fd.forEach((v, k) => {
      if (typeof v === 'string') {
        const s = v.trim();
        if (s !== '') obj[k] = s;
      }
    });
    // Resolve product GID from the selected suggestion. FormData
    // captured material_id from the hidden input; we upgrade it to a
    // Shopify GID and attach the display title for context.
    const hiddenInput = form.querySelector('[data-laser-settings-material-input]');
    const searchInput = form.querySelector('[data-laser-settings-material-search]');
    if (hiddenInput && hiddenInput.value) {
      obj.material_gid = `gid://shopify/Product/${hiddenInput.value}`;
      if (searchInput && searchInput.dataset.selectedTitle) {
        obj.material_title = searchInput.dataset.selectedTitle;
      }
    }
    delete obj.material_id; // raw numeric id is replaced by GID
    if (obj.website) delete obj.website;
    // Cast numeric fields
    ['power_percent', 'wattage', 'passes'].forEach(k => {
      if (obj[k] !== undefined) {
        const n = parseInt(obj[k], 10);
        if (!Number.isNaN(n)) obj[k] = n; else delete obj[k];
      }
    });
    return obj;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
